import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MailIcon, ZapIcon, CheckCircleIcon, CalendarIcon, MessageSquareIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-20 pb-16 lg:pt-0 lg:pb-0 dot-pattern" data-id="element-52">
      {/* Decorative background elements */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-primary/20 rounded-full blur-[100px] -z-10" data-id="element-53" />
      <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-[120px] -z-10" data-id="element-54" />

      {/* Floating chat bubbles */}
      <motion.div animate={{
      y: [0, -15, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="hidden lg:flex absolute top-32 right-[45%] bg-surface/80 backdrop-blur-sm border border-border p-3 rounded-2xl rounded-br-none shadow-lg items-center gap-2 text-sm text-gray-300" data-id="element-55">
        <MessageSquareIcon className="w-4 h-4 text-primary" data-id="element-56" />
        <span data-id="element-57">¿Tienen disponibilidad?</span>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 w-full" data-id="element-58">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center" data-id="element-59">
          {/* Left Column: Content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-start" data-id="element-60">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6" data-id="element-61">
              <ZapIcon className="w-4 h-4" data-id="element-62" />
              Automatización para Convertir Prospectos
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] mb-6 tracking-tight text-white" data-id="element-63">
              Deja de Perder Clientes. Convierte Interesados en Citas Agendadas
              —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary" data-id="element-64">
                Automáticamente.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-xl leading-relaxed" data-id="element-65">
              CONECKTA responde al instante, califica a tus prospectos y agenda
              citas sin que tengas que hacer seguimiento manual.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto" data-id="element-66">
              <button className="btn-cta w-full sm:w-auto group" data-id="element-67">
                Quiero Más Citas Agendadas
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-68" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-4 flex items-center gap-2" data-id="element-69">
              <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-70" />
              Llamada estratégica gratis · Sin compromiso
            </p>
          </motion.div>

          {/* Right Column: Pipeline Visual */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative w-full max-w-md mx-auto lg:ml-auto" data-id="element-71">
            <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent rounded-3xl -z-10 blur-xl" data-id="element-72" />

            <div className="flex flex-col gap-6 relative" data-id="element-73">
              {/* Connecting Line */}
              <div className="absolute left-[28px] top-[30px] bottom-[30px] w-0.5 bg-gradient-to-b from-primary via-secondary to-primary/20 z-0" data-id="element-74" />

              {/* Nodes */}
              {[{
              icon: MailIcon,
              title: 'Llega un Prospecto',
              desc: 'Vía anuncio o web',
              color: 'text-blue-400',
              bg: 'bg-blue-400/10',
              border: 'border-blue-400/30'
            }, {
              icon: ZapIcon,
              title: 'Respuesta Instantánea',
              desc: 'En menos de 5 seg',
              color: 'text-yellow-400',
              bg: 'bg-yellow-400/10',
              border: 'border-yellow-400/30',
              ml: 'ml-6'
            }, {
              icon: CheckCircleIcon,
              title: 'Prospecto Calificado',
              desc: 'Filtro automático',
              color: 'text-green-400',
              bg: 'bg-green-400/10',
              border: 'border-green-400/30',
              ml: 'ml-12'
            }, {
              icon: CalendarIcon,
              title: 'Cita Agendada',
              desc: 'Directo a tu calendario',
              color: 'text-primary',
              bg: 'bg-primary/20',
              border: 'border-primary/50',
              ml: 'ml-18',
              glow: true
            }].map((node, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4 + index * 0.15
            }} className={`relative z-10 flex items-center gap-4 bg-surface/90 backdrop-blur-sm border border-border p-4 rounded-2xl shadow-lg ${node.ml || ''} ${node.glow ? 'shadow-[0_0_30px_rgba(59,130,246,0.2)] border-primary/40' : ''}`} data-id="element-75">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${node.bg} ${node.border} border`} data-id="element-76">
                    <node.icon className={`w-6 h-6 ${node.color}`} data-id="element-77" />
                  </div>
                  <div data-id="element-78">
                    <h3 className="font-bold text-white text-lg" data-id="element-79">
                      {node.title}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-80">{node.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}